import { rootDomain } from "~/utils/creeper"

export const JOIN_BOX_COOKIE = "join_box_variant"
// Set by ?join_box= so a tester stays in that variant on every page and
// network site until the browser closes, whatever the site's percentage.
export const JOIN_BOX_OVERRIDE_COOKIE = "join_box_override"
const VARIANTS = ["control", "box"]
const OVERRIDES = { 1: "box", 0: "control" }

// Which version of the video page a guest gets while the embedded join box is
// tested: "control" (today's page) or "box". Returns the variant to show, and
// the one to remember in the cookie (null when nothing should be written).
export const chooseJoinBoxVariant = ({
  enabled,
  percent,
  override,
  forced,
  remembered,
  random = Math.random,
}) => {
  if (!enabled) return { variant: "control", remember: null }

  if (Object.hasOwn(OVERRIDES, override)) {
    return { variant: OVERRIDES[override], remember: OVERRIDES[override] }
  }

  if (VARIANTS.includes(forced)) return { variant: forced, remember: null }

  // Setting a site back to 0 reverts everyone, including visitors already
  // assigned to the box. Their cookie is kept for when it's turned back on.
  if (!(percent > 0)) return { variant: "control", remember: null }

  // At 100% everyone gets the box, including visitors remembered as control
  // from a smaller rollout, and nothing is written.
  if (percent >= 100) return { variant: "box", remember: null }

  if (VARIANTS.includes(remembered)) {
    return { variant: remembered, remember: null }
  }

  const variant = random() * 100 < percent ? "box" : "control"
  return { variant, remember: variant }
}

// Shared by everything that needs the visitor's variant. It stays "control"
// until assign() runs in the browser: CloudFront serves one cached page to
// every visitor, so the choice can't be made during SSR. Call it in setup.
export const useJoinBoxVariant = () => {
  const variant = useState("joinBoxVariant", () => "control")
  const currentSite = inject("currentSite", null)
  const route = useRoute()
  const config = useRuntimeConfig()

  const assign = () => {
    if (import.meta.server) return

    const override = route.query.join_box
    const choice = chooseJoinBoxVariant({
      enabled: config.public.joinBoxEnabled,
      percent: currentSite?.value?.joinBoxPercent,
      override,
      forced: readCookie(JOIN_BOX_OVERRIDE_COOKIE),
      remembered: readCookie(JOIN_BOX_COOKIE),
    })
    variant.value = choice.variant
    if (choice.remember) rememberVariant(choice.remember)
    if (Object.hasOwn(OVERRIDES, override)) forceVariant(OVERRIDES[override])
  }

  return { variant, assign }
}

const readCookie = (name) =>
  document.cookie
    .split(";")
    .map((cookie) => cookie.trim().split("="))
    .find(([key]) => key === name)?.[1]

// 30 days on the root domain, the same as the stan_session it's reported with.
const rememberVariant = (variant) => {
  const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
  const domain = rootDomain(window.location.hostname)
  document.cookie = `${JOIN_BOX_COOKIE}=${variant}; expires=${expires.toUTCString()}; domain=.${domain}; path=/`
}

// A session cookie, so a forced variant ends with the browser.
const forceVariant = (variant) => {
  const domain = rootDomain(window.location.hostname)
  document.cookie = `${JOIN_BOX_OVERRIDE_COOKIE}=${variant}; domain=.${domain}; path=/`
}
